import { appendEmsSessionIdQueryParameter } from '../../common/utils/commonUtilities';
import { UnAuthEntryPointToSourceAppMapping, Environment, SourceApps, URLs } from '../../web-legacy/models/constants/constants';
import { markets } from '../../web-legacy/models/constants/marketConstants';
import { UserContext } from '../../web-legacy/models/sharedInterfaces';
import { FreelanceRequest } from '../../web-legacy/packages/contactUs/models/requestModels';
import { SMCEol } from '../models/constants';
import { GetHelpSMCCultureMapping } from '../models/constants/getHelpConstants';
import { appContext } from '../models/getHelp/appContext';
import { urlParse } from './urlUtils';

export const getMetaTagAttributeValue = (metaTagName: string, attributeName: string) => {
  if (document.getElementsByName(metaTagName) && document.getElementsByName(metaTagName)[0]) {
    return document.getElementsByName(metaTagName)[0].getAttribute(attributeName);
  }
  return null;
};

export const isGetHelpSourceApp = (sourceapp: string) => {
  const getHelpSourceApps = [SourceApps.GetHelp1, SourceApps.GetHelpSkylight];
  sourceapp = !!sourceapp ? sourceapp.toLowerCase() : '';
  return !!sourceapp && getHelpSourceApps.indexOf(sourceapp as SourceApps) !== -1;
};

const validateSocMarket = (market: string): boolean => {
  // Be sure to null check market input before calling the method
  const marketInfo: string[] = market.split('-');
  return !!marketInfo && marketInfo.length >= 2;
};

const reformatSocMarket = (market: string): string => {
  let locale = market;
  const marketInfo: string[] = market.split('-');
  if (!!marketInfo && marketInfo.length < 2) {
    // navigator.language doesn't contain locale info
    Object.keys(markets).forEach((language: string) => {
      if (
        !!language &&
        !!markets[language] &&
        !!marketInfo[0] &&
        language.toLowerCase() === marketInfo[0].toLowerCase()
      ) {
        locale = marketInfo[0] + '-' + markets[language];
      }
    });
  } else if (!marketInfo) {
    locale = 'en-us';
  }
  return locale;
};

// Function to grab the value for a given cookie in document.cookie
export const getCookie = (cookieName: string, cookieString: string) => {
  const name = cookieName + '=';
  const decodedCookie = decodeURIComponent(cookieString);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    c = c.trim();
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
};

// Function to get the users market for a contact support session running in SOC
export const getSocMarket = () => {
  const sourceAppFromConfig = window.emeraldContext ? window.emeraldContext.sourceApp : '';
  if (isGetHelpSourceApp(sourceAppFromConfig)) {
    if (validateSocMarket(appContext.locale)) {
      return appContext.locale;
    } else {
      return reformatSocMarket(appContext.locale);
    }
  }
  // Check if market is in url path
  let urlPath = urlParse(window.location.href).path;
  if (urlPath !== null && urlPath.length > 1) {
    urlPath = urlPath.substring(1, urlPath.length);
    const pathArray = urlPath.split('/');
    if (
      pathArray.length > 0 &&
      pathArray[0].search('[a-zA-Z][a-zA-Z]-[a-zA-Z][a-zA-Z]') !== -1 &&
      validateSocMarket(pathArray[0])
    ) {
      return pathArray[0];
    }
  }

  // Check if market is in cookie string
  const mrktCookie = getCookie('omkt', document.cookie);
  if (mrktCookie !== null && validateSocMarket(mrktCookie)) {
    return mrktCookie;
  }

  // Check if browser language is detected, it may be null
  if (!!navigator && !!navigator.language) {
    if (validateSocMarket(navigator.language)) {
      return navigator.language;
    } else {
      return reformatSocMarket(navigator.language);
    }
  }

  // return default
  return 'en-us';
};

// send log to backend geneva
export const addGenevaLog = (logHandlerTags: object) => {
  if (!!logHandlerTags) {
    const xhr = new XMLHttpRequest();
    let market = getSocMarket();
    market = market ? market : 'en-us';
    const logResource = `/${market}${URLs.AddLogs}`;
    const url = window.emeraldContext && window.emeraldContext.apiEndpoint
      ? window.emeraldContext.apiEndpoint + logResource.slice(1) : logResource;
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
    xhr.send(JSON.stringify(logHandlerTags));
  }
};

export const getQueryParamDictionary = (urlString: string, skipKeys: string[] = [], lowerCaseKeys: boolean = true) => {
  let queryParamDictionary = urlParse(urlString, true).query as {
    [key: string]: string;
  };
  let skipKeysArray: string[] = !!skipKeys ? skipKeys : [];

  if (!!lowerCaseKeys) {
    const lowerCaseDictionary: { [key: string]: string } = {};
    for (const key in queryParamDictionary) {
      if (Object.prototype.hasOwnProperty.call(queryParamDictionary, key)) {
        lowerCaseDictionary[key.toLowerCase()] = queryParamDictionary[key];
      }
    }
    queryParamDictionary = lowerCaseDictionary;
    skipKeysArray = skipKeysArray.map((key: string) => (!!key ? key.toLowerCase() : ''));
  }

  // If skipKeys is passed in from the caller,
  // skip the query parameters from the skipKeys array.
  if (!!skipKeysArray && skipKeysArray.length > 0) {
    const skipKeysDictionary: { [key: string]: string } = {};
    for (const key in queryParamDictionary) {
      if (
        Object.prototype.hasOwnProperty.call(queryParamDictionary, key) &&
        // Only set the query parameter when key is NOT in the skipKeys array.
        skipKeysArray.indexOf(key) < 0
      ) {
        skipKeysDictionary[key] = queryParamDictionary[key];
      }
    }
    queryParamDictionary = skipKeysDictionary;
  }

  return queryParamDictionary;
};

// sourceapp=signup1 sends additional query params that will be set to:
// 1. ServiceRequest.MedaDatas
// 2. Omnichannel live chat widget custom context
export const getSignUpQueryParamsDictionary = (): { [key: string]: string } => {
  return getQueryParamDictionary(window.location.href, ['sourceapp', 'expguid']);
};

export const isThirdPartySourceApp = (sourceapp: string) => {
  // if we want to add more apps without code change, move this list to BRS
  const thirdPartySourceApps = [SourceApps.TechData1, SourceApps.TechData2, SourceApps.Synnex1];
  sourceapp = !!sourceapp ? sourceapp.toLowerCase() : '';
  return !!sourceapp && thirdPartySourceApps.indexOf(sourceapp as SourceApps) !== -1;
};

/**
 * Maps GetHelp locale to supported SMC EOL
 * @returns locale
 */
export const fetchGetHelpLocale = () => {
  const defaultLocale = 'en-us';
  let ghLocale = appContext.locale;
  if (!!ghLocale) {
    /**
     * 1. Check if SMCEol supports whatever locale was passed
     */
    const smclocale = SMCEol.find((l: string) => l.toLowerCase() === ghLocale.toLowerCase());
    if (!!smclocale) {
      return smclocale.toLowerCase();
    }

    /**
     * 2. Check for ll mapping and provide fallback from list
     */
    const mapping = GetHelpSMCCultureMapping;
    let key = Object.keys(mapping).find((c: string) => c.toLowerCase() === ghLocale.toLowerCase());
    if (mapping[key] && !!mapping[key][0]) {
      return mapping[key][0].toLowerCase();
    }

    /**
     * 3. Remove locale part only and check again
     */
    ghLocale = ghLocale.split('-')[0];
    if (ghLocale) {
      key = Object.keys(mapping).find((c: string) => c.toLowerCase() === ghLocale.toLowerCase());
      if (mapping[key] && !!mapping[key][0]) {
        return mapping[key][0].toLowerCase();
      }
    }
  }
  /**
   * 3. Return default en-us locale
   */
  return defaultLocale;
};

/**
 * Agave pane needs to wait for Office.onReady, this method returns locale
 * based on how we currently initialize market for different environments inside appInitializer
 */
export const getSynchronousLocale = () => {
  const queryParams = getQueryParamDictionary(window.location.href);
  const qpSourceApp = !!queryParams.sourceapp ? queryParams.sourceapp.toLowerCase() : '';
  const objectFromRazor = window['ResponsiveNavReact'];
  let locale = 'en-us';
  const sourceAppFromConfig = window.emeraldContext ? window.emeraldContext.sourceApp : '';
  // GetHelp
  if (isGetHelpSourceApp(sourceAppFromConfig)) {
    return fetchGetHelpLocale();
  }
  // SOC, 3p, Teams1 use getSocMarket
  if (
    window.location.href.indexOf(URLs.SOC) !== -1 ||
    window.location.href.indexOf(URLs.SMCExperiment) !== -1 ||
    window.location.href.indexOf(URLs.GroupMe) !== -1 ||
    isThirdPartySourceApp(qpSourceApp)
  ) {
    locale = getSocMarket();
  } else if (!!objectFromRazor) {
    // outlookDotCom and else logic for nonAgavepane env definition
    locale = objectFromRazor.market;
  } else {
    locale = getSocMarket();
  }
  return locale;
};

export const getSourceAppFromUrl = (urlString: string) => {
  const queryParamsDictionary = getQueryParamDictionary(urlString);
  const sourceApp = !!queryParamsDictionary.sourceapp ? queryParamsDictionary.sourceapp.toLowerCase() : '';
  if (
    !!sourceApp &&
    Object.keys(SourceApps)
      .map(app => SourceApps[app])
      .indexOf(sourceApp) !== -1
  ) {
    return sourceApp;
  } else if (urlString && urlString.toLowerCase().includes(URLs.Edu)) {
    return SourceApps.Edu;
  } else {
    return null;
  }
};

export const getUserContext = () => {
  const query: { [key: string]: string } = getQueryParamDictionary(window.location.href);
  let userContextObj: UserContext;
  let userContextString = query.clientcontext as string;
  try {
    userContextObj = JSON.parse(userContextString);
  } catch {
    console.log('error parsing user context string.');
  }
  return userContextObj;
};

// Remap Namespace to different SearchApplication depending on TeamsTenantSKU and TeamsUserLicenseType
export const TeamsContextToNamespaceMap = (userContext: UserContext): string => {
  const tenantSku: TeamsTenantSKU = (userContext && userContext.TeamsTenantSKU) || 'other';

  switch (tenantSku.toLowerCase()) {
    case 'enterprise':
      return 'MSFTTEAMS';
    case 'edu':
    case 'education':
      const licenseType: string = ((userContext && userContext.TeamsUserLicenseType) || 'unknown').toLowerCase();
      if (licenseType === 'teacher') {
        return 'MSTEAMSEDUTR';
      } else if (licenseType === 'student') {
        return 'MSTEAMSEDUST';
      } else {
        return 'MSTEAMSEDU';
      }
    case 'free':
      return 'MSTEAMSFR';
    case 'tfl':
      return 'MSTEAMSP';
    default:
      return 'MSFTTEAMS';
  }
};

/**
 * checks if html string is valid html string
 * @param html string
 * @returns
 */
export const isValidHTML = (html: string) => {
  html = html.replace(/\r?\n|\r/g, ''); // remove new \n
  html = html.replace(/<[^>]*\/\s?>/g, ''); // Remove all self closing tags
  html = html.replace(/<(br|hr|img).*?>/g, ''); // Remove all <br>, <hr>, and <img> tags
  // eslint-disable-next-line no-useless-escape
  const openingTags = html.match(/<[^\/].*?>/g) || []; // Get remaining opening tags
  const closingTags = html.match(/<\/.+?>/g) || []; // Get remaining closing tags

  return openingTags.length === closingTags.length ? true : false;
};

export const getOmnichannelChatLinkTemplate = (environment: Environment, openInAppOmnichannelChatInBrowser: boolean) => {
  const useSmcEndpoint = environment === Environment.agavePane
    || environment === Environment.OutlookDotCom
    || (environment === Environment.nonAgavePane && openInAppOmnichannelChatInBrowser);
  let link = window.emeraldContext && window.emeraldContext.apiEndpoint
    ? window.emeraldContext.apiEndpoint + URLs.OmnichannelLCWLink.slice(1)
    : (
      useSmcEndpoint
        ? `${URLs.SmcEndpoint}${URLs.OmnichannelLCWLink}`
        : URLs.OmnichannelLCWLink
    );

  return link;
};

/**
 * Get sourceapp or appver query string based on the input parameters.
 * 1. UnAuth entry points needs sourceapp query string, so back end can validate cookie with the ExtensionAttributes["OmnichannelLcwId"].
 *    URL like below:
 *    https://support.microsoft.com/en-us/home/livechatwidget?theme=signup_colorful&sourceapp=signup1&customcontext={2}
 * 2. InApp
 *    When open OC chat embedded within InApp, we need appver query string, so back end will know it is InApp and will load emeraldBootstrap.
 *    When open OC chat in browser, we don't need appver query string, as browser does not need emeraldBootstrap.
 * 3. Other entry points
 *    We don't need sourceapp or appver query string
 * @param source Source value from the service request
 * @param isClientApp Whether it is InApp
 * @param openInAppOmnichannelChatInBrowser Back end BRS OdcSup_ContactUs_OpenInAppOmnichannelChatInBrowser
 * @param appVer appVer value
 * @returns UnAuth: '&sourceapp=signup1', InApp: '&appver=ZWD900', Others: ''
 */
export const getSourceAppOrAppVerQueryString = (source: string, isClientApp: boolean, openInAppOmnichannelChatInBrowser: boolean, appVer: string) => {
  var queryString = '';
  var lowerCaseSource = !!source ? source.toLowerCase() : '';
  if (Object.keys(UnAuthEntryPointToSourceAppMapping).indexOf(lowerCaseSource) >= 0) {
    queryString = `&sourceapp=${UnAuthEntryPointToSourceAppMapping[lowerCaseSource]}`;
  } else if (isClientApp && !openInAppOmnichannelChatInBrowser) {
    queryString = `&appVer=${appVer}`;
  }

  return queryString;
};

// Gets the context object that will be sent to Omnichannel live chat widget as the &customcontext= query parameter
export const getOmnichannelContext = (request: FreelanceRequest) => {
  let context: Record<string, string>;

  // Add context for sourceapp=signup1
  const lowerCaseSource = !!request && !!request.Source ? request.Source.toLowerCase() : '';
  if (lowerCaseSource === 'signup') {
    context = getSignUpQueryParamsDictionary();
  }

  if (!context) {
    context = {};
  }
  // Add context for all entry points
  context.CaseNumber = request.RequestId;

  return context;
};

export const isDfCRequest = (requestId: string): boolean => {
  return !!requestId && requestId.length === 10 && (requestId.startsWith('70') || requestId.startsWith('79'));
};
